// src/components/AdminApplicationsPanel.jsx
//
// Admin view of Minecraft whitelist / RP applications, submitted from
// MinecraftApplicationPage.jsx. Lets staff filter by status, open a full
// application, and approve or reject it.
//
// Expected backend contract (not included in this bundle — wire these up
// server-side to match):
//   GET   /minecraft/rp-applications             -> { applications: [...] }
//   GET   /minecraft/rp-applications/:id         -> { application }
//   PATCH /minecraft/rp-applications/:id         body { status, staffNotes } -> { application }
// Each application is expected to carry all the fields collected by
// MinecraftApplicationPage's EMPTY_FORM, plus: id, status ("pending" |
// "approved" | "rejected"), created_at, reviewed_at, reviewNote, and
// optionally a nested `user` object ({ username, email }).

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  ClipboardList, Search, CheckCircle2, XCircle, Clock3, ChevronLeft,
  User, MessageSquare, ShieldCheck, BookOpen, Send,
  AlertTriangle, Mail, Globe2, CalendarDays, History,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { API, fmtDateTime, statusStyle } from "../utils/constants";

const TABS = [
  { id: "all",      label: "All" },
  { id: "pending",  label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

function StatusBadge({ status }) {
  const s = statusStyle(status);
  return (
    <span className="toa-admin-status" style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color }}>
      {status || "pending"}
    </span>
  );
}

/* ─── Full application review (takes over the admin body) ───────────────── */
function ApplicationReview({ application, onBack, onDecided, accessToken, showToast }) {
  const [note, setNote] = useState(application.staffNotes || application.reviewNote || "");
  const [saving, setSaving] = useState(null); // "approved" | "rejected" | null

  const decide = async (status) => {
    if (status === "rejected" && !note.trim()) {
      showToast("Add a short reason before rejecting — it's shown to the applicant.");
      return;
    }
    setSaving(status);
    try {
      const res = await fetch(`${API}/minecraft/rp-applications/${application.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
        credentials: "include",
        body: JSON.stringify({ status, staffNotes: note }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { showToast(data.error || "Could not update the application."); return; }
      showToast(status === "approved" ? "Application approved." : "Application rejected.");
      onDecided();
      onBack();
    } catch {
      showToast("Network error updating the application.");
    } finally {
      setSaving(null);
    }
  };

  const Section = ({ icon, title, children }) => (
    <div className="toa-sc" style={{ marginBottom: 14 }}>
      <div className="toa-st" style={{ justifyContent: "flex-start", gap: 8 }}>
        {icon} {title}
      </div>
      <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
        {children}
      </div>
    </div>
  );

  const Row = ({ label, value }) => (
    <div>
      <div style={{ fontSize: 10, color: "var(--muted)", fontFamily: "'Cinzel',serif", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{value || <span style={{ color: "var(--muted)" }}>—</span>}</div>
    </div>
  );

  const twoCol = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 };

  const sharedNetworkWarning = Boolean(application.hasSharedNetworkAcrossAccounts);

  return (
    <div className="toa-admin-page">
      <div className="toa-admin-bar">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button className="toa-btn toa-btn-ghost toa-admin-back" onClick={onBack}>
            <ChevronLeft style={{ width: 14, height: 14 }} /> Back
          </button>
          <span className="toa-admin-bar-title">Application #{application.id}</span>
        </div>
        <StatusBadge status={application.status} />
      </div>

      <div className="toa-admin-body">
        <div className="toa-editor-grid">
          <div>
            <Section icon={<User style={{ width: 13, height: 13 }} />} title="Linked Website Account">
              {application.applicantUserId ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 46, height: 46, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "rgba(123,94,167,.18)", border: "1px solid var(--borderB)", display: "grid", placeItems: "center" }}>
                      {application.accountProfilePicture ? (
                        <img src={application.accountProfilePicture} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <span style={{ fontFamily: "'Cinzel',serif", fontSize: 16, color: "var(--purpleB)", fontWeight: 700 }}>
                          {(application.accountUsername || "?")[0].toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontFamily: "'Cinzel',serif", fontSize: 14, color: "var(--text)", fontWeight: 700 }}>{application.accountUsername || "Unknown account"}</div>
                      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>Account ID #{application.applicantUserId}</div>
                    </div>
                  </div>
                  <div style={twoCol}>
                    <Row label="Account Email" value={application.accountEmail} />
                    <Row label="Account Role" value={application.accountRole} />
                    <Row label="Signup Country" value={application.accountSignupCountry} />
                    <Row label="Last Seen Country" value={application.accountLastCountry} />
                  </div>
                  <Row label="Account Created" value={application.accountCreatedAt ? fmtDateTime(application.accountCreatedAt) : null} />
                </>
              ) : (
                <div style={{ padding: "10px 12px", borderRadius: 8, background: "rgba(248,113,113,.07)", border: "1px solid rgba(248,113,113,.2)", color: "#fca5a5", fontSize: 12, lineHeight: 1.6 }}>
                  This application is not linked to a website account. This should only occur for legacy submissions created before account-linked applications were introduced.
                </div>
              )}
            </Section>

            <Section icon={<User style={{ width: 13, height: 13 }} />} title="Application Details">
              <div style={twoCol}>
                <Row label="Minecraft Username" value={application.minecraftUsername} />
                <Row label="Discord Username" value={application.discordUsername} />
                <Row label="Date of Birth" value={application.dateOfBirth} />
                <Row label="Timezone" value={application.timezone} />
                <Row label="Microphone" value={application.microphone} />
                <Row label="Weekly Availability" value={application.weeklyAvailability} />
              </div>
              <Row label="How they found us" value={application.referral} />
              <Row label="RP Experience" value={application.rpExperience} />
              <Row label="Previous Servers" value={application.previousServers} />
            </Section>

            <Section icon={<ShieldCheck style={{ width: 13, height: 13 }} />} title="Account & Safety Checks">
              <div style={twoCol}>
                <Row label="Submission Country" value={application.submissionCountry} />
                <Row label="Applications from this account" value={String((application.accountApplicationHistory || []).length || 0)} />
                <Row label="Applications from same network" value={application.networkSignalAvailable ? String(application.sameNetworkApplicationCount || 0) : "Not available"} />
                <Row label="Accounts seen on same network" value={application.networkSignalAvailable ? String(application.sameNetworkDistinctAccountCount || 0) : "Not available"} />
              </div>

              {sharedNetworkWarning ? (
                <div style={{ display: "flex", gap: 9, padding: "10px 12px", borderRadius: 8, background: "rgba(240,216,120,.08)", border: "1px solid rgba(240,216,120,.25)", color: "#f0d878", fontSize: 12, lineHeight: 1.55 }}>
                  <AlertTriangle style={{ width: 15, height: 15, flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <strong>Possible duplicate-account signal.</strong> Multiple website accounts have submitted applications from the same network. This is only a review flag — shared households, schools, workplaces, VPNs and mobile networks can legitimately share a network.
                  </div>
                </div>
              ) : application.networkSignalAvailable ? (
                <div style={{ fontSize: 12, color: "#86efac", padding: "8px 0" }}>No other applicant account is currently linked to the same network signal.</div>
              ) : (
                <div style={{ fontSize: 12, color: "var(--muted)" }}>No network signal is available for this legacy application.</div>
              )}

              {(application.accountApplicationHistory || []).length > 1 && (
                <div>
                  <div style={{ fontSize: 10, color: "var(--muted)", fontFamily: "'Cinzel',serif", letterSpacing: 1, textTransform: "uppercase", marginBottom: 7 }}>Application history for this account</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {application.accountApplicationHistory.map((row) => (
                      <div key={row.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 10px", borderRadius: 7, border: "1px solid var(--border)", background: "rgba(255,255,255,.02)", fontSize: 12 }}>
                        <span style={{ color: "var(--text2)" }}>App #{row.id} · {row.minecraftUsername || "—"}</span>
                        <span style={{ color: "var(--muted)", flexShrink: 0 }}>{row.status || "pending"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            <Section icon={<BookOpen style={{ width: 13, height: 13 }} />} title="Character">
              <div style={twoCol}>
                <Row label="Name" value={application.characterName} />
                <Row label="Age" value={application.characterAge} />
              </div>
              <Row label="Origin" value={application.characterOrigin} />
              <Row label="Occupation" value={application.characterOccupation} />
              <Row label="Backstory" value={application.characterBackstory} />
              <Row label="Personality" value={application.characterPersonality} />
              <Row label="Goals" value={application.characterGoals} />
              <Row label="Flaws" value={application.characterFlaws} />
            </Section>

            <Section icon={<ShieldCheck style={{ width: 13, height: 13 }} />} title="RP Knowledge">
              <Row label="Metagaming" value={application.metagaming} />
              <Row label="Powergaming" value={application.powergaming} />
              <Row label="Fail RP" value={application.failRp} />
              <Row label="Value of Life" value={application.valueOfLife} />
            </Section>

            <Section icon={<MessageSquare style={{ width: 13, height: 13 }} />} title="Scenarios">
              <Row label="Conflict" value={application.scenarioConflict} />
              <Row label="Information" value={application.scenarioInformation} />
              <Row label="Robbery" value={application.scenarioRobbery} />
              <Row label="Rule-break" value={application.scenarioRulebreak} />
              <Row label="Why TOA?" value={application.whyTOA} />
            </Section>
          </div>

          {/* sidebar: decision panel */}
          <div className="toa-editor-sidebar">
            <div className="toa-sc" style={{ marginBottom: 14 }}>
              <div className="toa-st">Submission</div>
              <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                <Row label="Submitted" value={fmtDateTime(application.createdAt || application.created_at)} />
                {(application.reviewedAt || application.reviewed_at) && <Row label="Last Reviewed" value={fmtDateTime(application.reviewedAt || application.reviewed_at)} />}
                <Row label="Website Account" value={application.accountUsername ? `${application.accountUsername} (#${application.applicantUserId})` : null} />
              </div>
            </div>

            <div className="toa-sc" style={{ marginBottom: 14 }}>
              <div className="toa-st">Decision</div>
              <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text2)", marginBottom: 6, fontFamily: "'Cinzel',serif", textTransform: "uppercase", letterSpacing: ".5px" }}>
                    Note to applicant {"("}required to reject{")"}
                  </div>
                  <textarea
                    rows={4}
                    placeholder="e.g. Character backstory needs more detail — feel free to reapply."
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    style={{
                      width: "100%", background: "rgba(255,255,255,.04)", border: "1px solid var(--border)",
                      borderRadius: 7, padding: "10px 12px", fontSize: 13, color: "var(--text)",
                      fontFamily: "Inter,sans-serif", outline: "none", boxSizing: "border-box", resize: "vertical",
                    }}
                  />
                </div>
                <button
                  className="toa-btn"
                  style={{ width: "100%", background: "linear-gradient(135deg,#1a5c2e,#2f9e57)", color: "#eafff0", border: "1px solid rgba(74,222,128,.4)", opacity: saving ? .6 : 1 }}
                  disabled={!!saving}
                  onClick={() => decide("approved")}
                >
                  <CheckCircle2 style={{ width: 14, height: 14 }} /> {saving === "approved" ? "Approving…" : "Approve"}
                </button>
                <button
                  className="toa-btn"
                  style={{ width: "100%", background: "linear-gradient(135deg,#7f1d1d,#991b1b)", color: "#fca5a5", border: "1px solid rgba(239,68,68,.35)", opacity: saving ? .6 : 1 }}
                  disabled={!!saving}
                  onClick={() => decide("rejected")}
                >
                  <XCircle style={{ width: 14, height: 14 }} /> {saving === "rejected" ? "Rejecting…" : "Reject"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── List + filters ──────────────────────────────────────────────────────── */
export default function AdminApplicationsPanel({ showToast }) {
  const { accessToken } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("pending");
  const [query, setQuery] = useState("");
  const [reviewing, setReviewing] = useState(null);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/minecraft/rp-applications?limit=100`, {
        headers: { Authorization: `Bearer ${accessToken}` }, credentials: "include",
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setApplications(data.applications || []);
      setError("");
    } catch {
      setApplications([]);
      setError("Could not load applications.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const counts = useMemo(() => ({
    all: applications.length,
    pending: applications.filter(a => (a.status || "pending") === "pending").length,
    approved: applications.filter(a => a.status === "approved").length,
    rejected: applications.filter(a => a.status === "rejected").length,
  }), [applications]);

  const [openingId, setOpeningId] = useState(null);

  const openApplication = async (id) => {
    setOpeningId(id);
    try {
      const res = await fetch(`${API}/minecraft/rp-applications/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` }, credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showToast(data.error || "Could not load the application.");
        return;
      }
      setReviewing(data.application || null);
    } catch {
      showToast("Network error loading the application.");
    } finally {
      setOpeningId(null);
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = applications
    .filter(a => tab === "all" || (a.status || "pending") === tab)
    .filter(a => !q ||
      a.minecraftUsername?.toLowerCase().includes(q) ||
      a.discordUsername?.toLowerCase().includes(q) ||
      a.accountUsername?.toLowerCase().includes(q) ||
      a.accountEmail?.toLowerCase().includes(q)
    )
    .sort((a, b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at));

  if (reviewing) {
    return (
      <ApplicationReview
        application={reviewing}
        onBack={() => setReviewing(null)}
        onDecided={fetchApplications}
        accessToken={accessToken}
        showToast={showToast}
      />
    );
  }

  return (
    <>
      <div className="toa-admin-stats">
        {[
          { label: "Pending",  val: counts.pending,  color: "#f0d878" },
          { label: "Approved", val: counts.approved, color: "#4ade80" },
          { label: "Rejected", val: counts.rejected, color: "#f87171" },
          { label: "Total",    val: counts.all,       color: "var(--gold)" },
        ].map(s => (
          <div key={s.label} className="toa-sc toa-admin-stat">
            <div className="toa-admin-stat-val" style={{ color: s.color }}>{s.val}</div>
            <div className="toa-admin-stat-lbl">{s.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
        <div style={{ display: "flex", border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "8px 16px", fontSize: 11, fontFamily: "'Cinzel',serif", letterSpacing: .8,
              textTransform: "uppercase", cursor: "pointer", border: "none", transition: ".15s",
              background: tab === t.id ? "linear-gradient(135deg,var(--purple),#5a3d8a)" : "transparent",
              color: tab === t.id ? "#fff" : "var(--muted)",
            }}>
              {t.label}{t.id !== "all" && ` (${counts[t.id]})`}
            </button>
          ))}
        </div>
        <div style={{ position: "relative", flex: 1, minWidth: 180 }}>
          <Search style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", width: 14, height: 14, color: "var(--muted)", pointerEvents: "none" }} />
          <input
            type="text" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search Minecraft, Discord, account or email…"
            style={{
              width: "100%", background: "rgba(255,255,255,.04)", border: "1px solid var(--border)",
              borderRadius: 8, padding: "9px 12px 9px 34px", fontSize: 13, color: "var(--text)",
              fontFamily: "Inter,sans-serif", outline: "none", boxSizing: "border-box",
            }}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ color: "var(--muted)", fontSize: 14, padding: "40px 0", textAlign: "center" }}>Loading applications…</div>
      ) : error ? (
        <div style={{ color: "#f87171", fontSize: 13, padding: "20px 0" }}>{error}</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "70px 20px" }}>
          <ClipboardList style={{ width: 40, height: 40, opacity: .25, marginBottom: 14, color: "var(--muted)" }} />
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 16, color: "var(--text2)", marginBottom: 8 }}>No applications here</div>
          <div style={{ fontSize: 13, color: "var(--muted)" }}>
            {tab === "pending" ? "You're all caught up — nothing waiting on review." : "Nothing matches this filter yet."}
          </div>
        </div>
      ) : (
        <div className="toa-admin-list">
          {filtered.map(app => {
            const appDate = new Date(app.createdAt || app.created_at);
            const { day, mon } = { day: Number.isNaN(appDate.getTime()) ? "--" : appDate.getDate().toString().padStart(2, "0"), mon: Number.isNaN(appDate.getTime()) ? "" : appDate.toLocaleString("en-GB", { month: "short" }).toUpperCase() };
            return (
              <div key={app.id} className="toa-admin-row" style={{ cursor: "pointer" }} onClick={() => openApplication(app.id)}>
                <div className="toa-admin-date">
                  <div className="toa-pday" style={{ fontSize: 16 }}>{day}</div>
                  <div className="toa-pmon" style={{ fontSize: 9 }}>{mon}</div>
                </div>
                <div className="toa-admin-row-info">
                  <div className="toa-admin-row-title">
                    {(app.status || "pending") === "pending" && <Clock3 style={{ width: 11, height: 11, marginRight: 5, color: "#f0d878", verticalAlign: "middle", flexShrink: 0 }} />}
                    {app.minecraftUsername || "Unknown player"}
                  </div>
                  <div className="toa-admin-row-meta">
                    Account <span style={{ color: "var(--goldL)" }}>{app.accountUsername || "Legacy / unlinked"}</span>
                    {" · "}Discord <span style={{ color: "var(--purpleB)" }}>{app.discordUsername || "—"}</span>
                    {" · "}Character: {app.characterName || "—"}
                  </div>
                </div>
                <StatusBadge status={app.status || "pending"} />
                <div className="toa-admin-actions">
                  <button title="Review application" className="toa-admin-action" style={{ color: "var(--purpleB)" }} disabled={openingId === app.id} onClick={(e) => { e.stopPropagation(); openApplication(app.id); }}>
                    {openingId === app.id ? <Clock3 style={{ width: 13, height: 13 }} /> : <Send style={{ width: 13, height: 13 }} />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}